<template>
  <div class="container is-fluid">
    <div>
      <back-link/>
      <h3 class="title">Outstanding</h3>
      <div class="level">
        <div class="level-left">
          <a
            id="download"
            style="display: none;"
            download="kyc_outstanding.csv"
            @click="downloadCsv"
          ></a>
          <b-tooltip label="Expand details" type="is-dark">
            <a
              href=""
              title="Expand details"
              class="icon"
              @click.prevent="expandDetails=!expandDetails">
              <b-icon :icon="expandDetails ? 'playlist-minus' : 'playlist-plus'"></b-icon>
            </a>
          </b-tooltip>
          <b-tooltip label="Download as CSV" type="is-dark">
            <a
              href=""
              title="Download CSV"
              class="icon"
              @click.prevent="downloadCsv"><b-icon icon="content-save"/></a>
          </b-tooltip>
        </div>
        <div class="level-right">
          <file-manager-select
            label="Filter by File Manager"
            :value="fileManagerId"
            @value-changed="setFileManagerId"
          />
        </div>
      </div>
    </div>

    <b-table
      :data="filteredForms"
      detailed
      :opened-detailed="defaultOpenedDetails"
      detail-key="id"
    >

      <template slot-scope="props">
        <b-table-column field="index" label="#">
          {{props.index + 1}}
        </b-table-column>

        <b-table-column field="name" label="Client Name" sortable>
          <router-link :to="{ name: 'details', params: { kycId: props.row.id }}">
            {{props.row.name}}
          </router-link>
        </b-table-column>

        <b-table-column field="job_number" label="WFM Job Number" sortable>
          {{props.row.job_number}}
        </b-table-column>

        <b-table-column field="file_manager_id" label="File Manager" sortable :custom-sort="sortByStaffName">
          {{$store.getters.getStaffNameById(props.row.file_manager_id)}}
        </b-table-column>

        <b-table-column field="date_modified" label="Modified" sortable>
          {{formatDate(props.row.date_modified)}}
        </b-table-column>
      </template>

      <template slot="detail" slot-scope="props">
        <ol>
          <li
            v-for="(doc, idx) in props.row.docs_outstanding"
            :key="`${props.row.job_number}_${idx}`"
          >
            <svg-mark :value="doc.status" style="position:relative;top:10px;height:30px;"/>
            {{ doc.docDesc.replace('{0}','') }} for <b>{{ doc.name }}</b>
          </li>
          <li v-for="(search, idx) in props.row.searches"
            :key="`searches_${props.row.job_number}_${idx}`">
            <svg-mark :value="0" style="position:relative;top:10px;height:30px;"/>
            {{ formatSearchName(search.searchId) }}
          </li>
        </ol>
      </template>
    </b-table>
  </div>
</template>

<script>
import moment from 'moment'
import changeCase from 'change-case'
import BackLink from '@/components/BackLink'
import SvgMark from '@/components/KycDetailsSvgMark'
import FileManagerSelect from '@/components/FileManagerSelect'

export default {
  name: 'Report',
  components: {
    BackLink,
    SvgMark,
    FileManagerSelect
  },
  data () {
    return {
      forms: [],
      columns: {
        'name': 'Name',
        'job_number': 'WFM Number',
        'file_manager_id': 'File Manager',
        'doc_desc': 'Document Type',
        'entity_name': 'Relevant Person / Company Name',
        'status': 'Status'
      },
      expandDetails: false,
      fileManagerId: null
    }
  },
  created () {
    this.getListOfForms()
  },
  mounted () {
    if (this.$store.state.staff.length === 0) {
      this.$store.dispatch('getStaff')
    }
  },
  computed: {
    formsData () {
      if (!this.forms) return
      // const formData = this.forms.map(form => this.getColumnValues(form))

      const data = []
      for (const form of this.forms) {
        const rows = []
        for (const doc of form.docs_outstanding) {
          rows.push([
            form.name,
            form.job_number,
            this.$store.getters.getStaffNameById(form.file_manager_id),
            doc.docDesc,
            doc.name,
            doc.status === 0 ? '' : doc.status === 10 ? 'copy' : '?'
          ])
        }
        for (const search of form.searches) {
          rows.push([
            form.name,
            form.job_number,
            this.$store.getters.getStaffNameById(form.file_manager_id),
            this.formatSearchName(search.searchId),
            null,
            null
          ])
        }
        data.push(...rows)
      }

      return data
    },
    defaultOpenedDetails () {
      if (this.expandDetails) {
        return this.forms.map(form => { return form.id })
      }
    },
    filteredForms () {
      if (this.fileManagerId) {
        return this.forms.filter(form => {
          return form.file_manager_id === this.fileManagerId
        })
      }
      return this.forms
    }
  },
  methods: {
    getListOfForms () {
      this.$store.dispatch('setIsLoading', { isLoading: true })
      this.axios.get('/kyc/report')
        .then(response => {
          this.$store.dispatch('setIsLoading', { isLoading: false })
          if (response.data) {
            this.forms = response.data
          }
        })
        .catch(error => {
          console.log(error)
          this.$store.dispatch('setIsLoading', { isLoading: false })
        })
    },
    // getColumnValues (form) {
    //   const columnKeys = Object.keys(this.columns)
    //   return columnKeys.map(col => {
    //     if (col === 'file_manager_id' && form[col]) return this.$store.getters.getStaffNameById(form[col])
    //     return form[col] ? form[col] : ''
    //   })
    // },
    arrayToCsv () {
      if (this.formsData.length < 0) return

      const columnValues = Object.values(this.columns)
      const escapedHeaders = columnValues.map(text => this.escapeText(text))
      const headers = escapedHeaders.join(',') + '\n'

      const escapedArray = this.formsData.map(fields => fields.map(this.escapeText))
      let csv = escapedArray.map(fields => fields.join(',')).join('\n')
      csv = headers + csv
      return csv
    },
    escapeText (text) {
      if (!text) return null
      text = text
        .replace(/\\/g, '\\\\')
        .replace(/\n/g, '\\n')
        .replace(/,/g, '\,') // eslint-disable-line no-useless-escape
        .replace(/"/g, '\\"')
        .replace(/'/g, '\\\'')
        .replace('{0} ', '')
      return `"${text}"`
    },
    downloadCsv () {
      const csv = this.arrayToCsv()
      const download = document.getElementById('download')
      download.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv))
      download.click()
    },
    sortByStaffName (a, b, desc) {
      const staffNameA = this.$store.getters.getStaffNameById(a.file_manager_id)
      const staffNameB = this.$store.getters.getStaffNameById(b.file_manager_id)
      const orderDir = desc ? 1 : -1
      if (!staffNameA || !staffNameB) return 1 * orderDir
      if (staffNameA > staffNameB) return 1 * orderDir
      if (staffNameA < staffNameB) return -1 * orderDir
      return 0
    },
    formatDate (date) {
      return moment(date).fromNow()
    },
    formatSearchName (name) {
      return `${changeCase.title(name)} Searches`
    },
    setFileManagerId (fileManagerId) {
      this.fileManagerId = fileManagerId
    }
  }
}
</script>

<style scoped>

</style>
